import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import MuiTableCell, { TableCellProps } from '@mui/material/TableCell';
import MuiTableRow, { TableRowOwnProps } from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { Skeleton } from '~components/Skeleton/Skeleton';
import { NET_RATE_FRACTION_DIGITS } from '~constants/consts';
import { InvoiceLineItemNet } from '~hooks/useInvoiceLineItems/models';

interface LineItemsTableFooterItemProps {
  label: string;
  size?: 'medium' | 'large';
  value: string;
}

export const LineItemsTableFooterItem = ({
  label,
  size = 'medium',
  value,
}: LineItemsTableFooterItemProps) => {
  return (
    <Box
      sx={{
        px: 1.5,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 3,
      }}
    >
      <Typography
        sx={{
          flex: 1,
          fontSize: size === 'medium' ? '13px' : '14px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          flex: 0,
          fontSize: size === 'medium' ? '13px' : '14px',
          fontWeight: 600,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

interface LineItemsTableTableRowProps extends TableRowOwnProps {
  isHeader?: boolean;
}

export const LineItemsTableTableRow = styled(MuiTableRow, {
  shouldForwardProp: (propName) => propName !== 'isHeader',
})<LineItemsTableTableRowProps>(({ theme, isHeader }) => ({
  '&.MuiTableRow-root': {
    backgroundColor: isHeader
      ? theme.semantic.surfaceSunken
      : theme.palette.background.paper,
  },
}));

export interface LineItemsTableCellProps extends TableCellProps {
  isHeader?: boolean;
  minWidth?: string;
  textAlign?: 'left' | 'center' | 'right';
  width?: string;
}

export const LineItemsTableCell = styled(MuiTableCell, {
  shouldForwardProp: (propName) =>
    !['isHeader', 'minWidth', 'textAlign', 'width'].includes(String(propName)),
})<LineItemsTableCellProps>(
  ({ theme, isHeader, textAlign = 'left', width, minWidth }) => ({
    '&.MuiTableCell-root': {
      borderBottom: 0,
      borderTopColor: theme.palette.divider,
      borderTopStyle: 'solid',
      borderTopWidth: isHeader ? 0 : 1,
      fontSize: '11px',
      fontWeight: isHeader ? 600 : 400,
      minWidth,
      padding: theme.spacing(1),
      textAlign,
      width,
    },
  }),
);

interface LineItemsTableHeader {
  text: string;
  textAlign: LineItemsTableCellProps['textAlign'];
  textForEditMode?: string;
  width?: string;
  minWidth?: string;
}

export const withNetColumnHeaders = (
  headers: LineItemsTableHeader[],
  showNetColumns: boolean,
): LineItemsTableHeader[] =>
  showNetColumns
    ? [
        ...headers.slice(0, -1),
        {
          text: t('pay_commission_statement.commission'),
          textAlign: 'right',
          width: '90px',
        },
        {
          text: t('pay_commission_statement.net_rate'),
          textAlign: 'right',
          width: '100px',
        },
        ...headers.slice(-1),
      ]
    : headers;

interface NetRateCellsProps {
  currencyFormatter: (value: number, options?: Intl.NumberFormatOptions) => string;
  lineItemNet?: InvoiceLineItemNet;
}

export const NetRateCells = ({ currencyFormatter, lineItemNet }: NetRateCellsProps) => (
  <>
    <LineItemsTableCell textAlign="right">
      {lineItemNet?.payCommissionPercentage
        ? `${Number(lineItemNet.payCommissionPercentage)}%`
        : ''}
    </LineItemsTableCell>
    <LineItemsTableCell textAlign="right">
      {lineItemNet?.payCommissionNetRate
        ? currencyFormatter(Number(lineItemNet.payCommissionNetRate), {
            maximumFractionDigits: NET_RATE_FRACTION_DIGITS,
          })
        : ''}
    </LineItemsTableCell>
  </>
);

export const LineItemsTableSkeleton = () => (
  <Box
    sx={(theme) => ({
      border: `solid 1px ${theme.palette.divider}`,
      borderRadius: 0.5,
    })}
  >
    <LineItemsTableSkeletonRow />
    <Divider />
    <LineItemsTableSkeletonRow />
    <LineItemsTableSkeletonRow />
  </Box>
);

const LineItemsTableSkeletonRow = () => (
  <Box
    sx={{
      display: 'flex',
      gap: 3,
      width: '100%',
      p: 1,
    }}
  >
    {Array.from({ length: 5 }).map((_, index) => (
      <Skeleton key={index} variant="text" sx={{ fontSize: '1rem', flex: 1 }} />
    ))}
  </Box>
);
